import { useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useLazyQuery } from '@apollo/client'
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons'
import { observer } from 'mobx-react'

import { Alert, Card, CardHeader } from 'Components/_theme'

import type {
  getDeviceReportPdf,
  getDeviceReportPdfVariables,
} from 'Constants/graphql/queries/detail/__generated__/getDeviceReportPdf'
import { GET_DEVICE_REPORT_PDF } from 'Constants/graphql/queries/detail/GetDeviceReportPdf'

import { buttonize, formatDate } from 'Portal/src/utilities'

import type { DataErasureCardProps } from './DataErasureCard.types'
import { getStatusTranslationKey } from './DataErasureCard.utils'

export const DataErasureCard = observer((props: DataErasureCardProps) => {
  const { deviceReport } = props
  const [showError, setShowError] = useState(false)

  const statusTranslationKey = useMemo(
    () => getStatusTranslationKey(deviceReport?.status),
    [deviceReport?.status],
  )

  const [getDeviceReportPdf, { error: getDeviceReportPdfError }] = useLazyQuery<
    getDeviceReportPdf,
    getDeviceReportPdfVariables
  >(GET_DEVICE_REPORT_PDF, {
    onCompleted: data => {
      const { fileName, fileData } = data.getDeviceReportPdf || {}

      if (!fileName || !fileData) {
        setShowError(true)

        return
      }

      // Decode Base64 to binary
      const byteCharacters = atob(fileData)
      const byteNumbers = Array.from({ length: byteCharacters.length })
        .fill(0)
        .map((_, i) => byteCharacters.charCodeAt(i))
      const byteArray = new Uint8Array(byteNumbers)

      const blob = new Blob([byteArray], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')

      a.href = url
      a.download = fileName || 'data-erasure-report.pdf'
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    },
  })

  const handleDownloadReport = (
    id: getDeviceReportPdfVariables['id'],
    fileName: getDeviceReportPdfVariables['fileName'],
  ) => {
    setShowError(false)
    getDeviceReportPdf({ variables: { fileName, id } })
  }

  if (!deviceReport) {
    return null
  }

  const { fileName, id } = deviceReport

  return (
    <Card className="DataErasureCard mb-6">
      <CardHeader>
        <FormattedMessage id="DataErasureCard_header" />
      </CardHeader>

      <div className="DataErasureCard-body">
        {(showError || !!getDeviceReportPdfError) && (
          <Alert
            className="mb-6"
            iconLeft={faExclamationCircle}
            variant="danger"
          >
            <FormattedMessage id="GenericErrorMessage" />
          </Alert>
        )}

        <div className="mb-2">
          <span className="DataErasureCard-title fw-normal">
            <FormattedMessage id="DataErasureCard_ErasureId" />:
          </span>

          <span className="ps-2 text-break">
            {deviceReport.fileName && deviceReport.fileName ? (
              <span
                {...buttonize(() => handleDownloadReport(id, fileName))}
                className="text-primary text-uppercase"
              >
                {deviceReport.identifier}
              </span>
            ) : (
              deviceReport.identifier
            )}
          </span>
        </div>

        <div className="mb-2">
          <span className="DataErasureCard-title fw-normal">
            <FormattedMessage id="DataErasureCard_ErasureDate" />:
          </span>

          <span className="ps-2">{formatDate(deviceReport.processedOn)}</span>
        </div>

        <div>
          <span className="DataErasureCard-title fw-normal">
            <FormattedMessage id="DataErasureCard_ErasureStatus" />:
          </span>

          <span className="ps-2">
            {statusTranslationKey ? (
              <FormattedMessage id={statusTranslationKey} />
            ) : (
              '-'
            )}
          </span>
        </div>
      </div>
    </Card>
  )
})
